/**
 * AddMilestoneModal Component
 * Modal for adding a new custom payment milestone
 */

import React, { useState } from 'react';
import { paymentMilestoneAPI } from '../../services/api';
import { formatCurrency } from '../../utils/formatters';
import { toast } from 'react-toastify';

const AddMilestoneModal = ({ isOpen, onClose, onSuccess, projectId, packagePrice }) => {
  const [formData, setFormData] = useState({
    milestoneType: 'progress',
    milestoneName: '',
    targetPercentage: '',
    targetAmount: '',
    dueDate: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  // Handle percentage change - auto-calculate amount
  const handlePercentageChange = (value) => {
    const percentage = parseFloat(value) || 0;
    const calculatedAmount = packagePrice > 0 ? (packagePrice * percentage / 100).toFixed(2) : '';
    setFormData({
      ...formData,
      targetPercentage: value,
      targetAmount: calculatedAmount
    });
  };

  // Handle amount change - auto-calculate percentage
  const handleAmountChange = (value) => {
    const amount = parseFloat(value) || 0;
    const calculatedPercentage = packagePrice > 0 ? ((amount / packagePrice) * 100).toFixed(2) : '';
    setFormData({
      ...formData,
      targetAmount: value,
      targetPercentage: calculatedPercentage
    });
  };

  // Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.milestoneName.trim()) {
      toast.error('Please enter a milestone name');
      return;
    }

    if (!formData.targetPercentage || parseFloat(formData.targetPercentage) <= 0) {
      toast.error('Please enter a valid target percentage');
      return;
    }

    if (parseFloat(formData.targetPercentage) > 100) {
      toast.error('Target percentage cannot exceed 100%');
      return;
    }

    setSubmitting(true);

    try {
      await paymentMilestoneAPI.create(projectId, {
        milestoneType: formData.milestoneType,
        milestoneName: formData.milestoneName,
        targetPercentage: parseFloat(formData.targetPercentage),
        dueDate: formData.dueDate || null,
        notes: formData.notes || null
      });

      onSuccess();
    } catch (err) {
      console.error('Error creating milestone:', err);
      toast.error(err.message || 'Failed to create milestone');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-gray-500 bg-opacity-75 flex items-end sm:items-center justify-center">
      {/* Backdrop - only for desktop clicking outside */}
      <div
        className="absolute inset-0"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full sm:max-w-lg sm:mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        <form onSubmit={handleSubmit} className="flex flex-col max-h-[90vh]">
          {/* Header - Sticky */}
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200 flex-shrink-0">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">
              Add Payment Milestone
            </h3>
          </div>

          {/* Content - Scrollable */}
          <div className="px-4 sm:px-6 py-4 overflow-y-auto flex-1">
            {/* Package Price Info */}
            {packagePrice > 0 && (
              <div className="bg-gray-50 rounded-lg p-3 mb-4 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Package Price:</span>
                  <span className="font-medium">{formatCurrency(packagePrice)}</span>
                </div>
              </div>
            )}

            {/* Form Fields */}
            <div className="space-y-4">
              {/* Milestone Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Milestone Name *
                </label>
                <input
                  type="text"
                  value={formData.milestoneName}
                  onChange={(e) => setFormData({ ...formData, milestoneName: e.target.value })}
                  disabled={submitting}
                  placeholder="e.g., 25% Progress Payment"
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 text-base min-h-[48px] touch-manipulation"
                  autoFocus
                />
              </div>

              {/* Milestone Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Milestone Type *
                </label>
                <select
                  value={formData.milestoneType}
                  onChange={(e) => setFormData({ ...formData, milestoneType: e.target.value })}
                  disabled={submitting}
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 text-base min-h-[48px] touch-manipulation"
                >
                  <option value="lock_price">Lock Price</option>
                  <option value="site_commencement">Site Commencement</option>
                  <option value="progress">Progress Payment</option>
                  <option value="final_completion">Final Completion</option>
                </select>
              </div>

              {/* Target Amount & Percentage - linked fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cumulative Target (RM)
                  </label>
                  <input
                    type="number"
                    value={formData.targetAmount}
                    onChange={(e) => handleAmountChange(e.target.value)}
                    disabled={submitting}
                    min="0"
                    step="0.01"
                    placeholder="Enter amount"
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 text-base min-h-[48px] touch-manipulation"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cumulative % of Package
                  </label>
                  <input
                    type="number"
                    value={formData.targetPercentage}
                    onChange={(e) => handlePercentageChange(e.target.value)}
                    disabled={submitting}
                    min="0"
                    max="100"
                    step="0.01"
                    placeholder="e.g., 60 for 60%"
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 text-base min-h-[48px] touch-manipulation"
                  />
                </div>
              </div>
              {packagePrice > 0 && (
                <p className="text-xs text-gray-500 -mt-2">
                  Cumulative percentage: total % of package price to be paid by this milestone (e.g., 60% means 60% of package should be paid by this point)
                </p>
              )}

              {/* Due Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Due Date (Optional)
                </label>
                <input
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                  disabled={submitting}
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 text-base min-h-[48px] touch-manipulation"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes (Optional)
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  disabled={submitting}
                  rows={2}
                  placeholder="Additional notes"
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 text-base touch-manipulation"
                />
              </div>
            </div>
          </div>

          {/* Footer - Sticky */}
          <div className="px-4 sm:px-6 py-4 border-t border-gray-200 bg-gray-50 flex flex-col-reverse sm:flex-row sm:justify-end gap-3 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="w-full sm:w-auto px-4 py-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 active:scale-[0.98] min-h-[48px] touch-manipulation transition-all disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="w-full sm:w-auto px-4 py-3 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 active:scale-[0.98] min-h-[48px] touch-manipulation transition-all disabled:opacity-50"
            >
              {submitting ? 'Creating...' : 'Add Milestone'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddMilestoneModal;
